import './SonarText.scss';

import classNames from 'classnames';
import { EllipsisConfig } from 'antd/es/typography/Base';
import { isBoolean } from '@sonar-software/sonar-ts-lib';
import ResizeObserver from 'rc-resize-observer';
import { TextProps } from 'antd/es/typography/Text';
import { TooltipProps } from 'antd/lib';

import React, { useCallback, useRef, useState } from 'react';
import { Tooltip, Typography } from 'antd';
import { useOnMount, useOnUnmount } from '@hooks';

const { Link, Text } = Typography;

export type FontSize = 12 | 14 | 16 | 20 | 24 | 30 | 38 | 46 | 56;
export type FontWeight = 'semibold';

export type SonarTextProps = TextProps & {
	link?: boolean;
	size?: FontSize;
	weight?: FontWeight;
};

export const SonarText: React.FC<SonarTextProps> = ({
	children,
	ellipsis,
	link,
	size = 14,
	weight,
	className,
	type,
	...props
}) => {
	const spanRef = useRef<HTMLSpanElement>(null);

	const textWeight = weight ? `-${weight}` : '';
	const textClassNames = classNames(
		`sonar-text text-${size}${textWeight}`,
		{ secondary: type === 'secondary' },
		className
	);

	const [ hasOverflow, setHasOverflow ] = useState(false);

	const handleResize = useCallback(() => {
		const container = spanRef.current;

		if (!container) {
			return;
		}

		setHasOverflow(container.offsetWidth < container.scrollWidth);
	}, []);

	useOnMount(() => {
		window.addEventListener('resize', handleResize);
	});

	useOnUnmount(() => {
		window.removeEventListener('resize', handleResize);
	});

	if (link) {
		const { onClick, ...linkProps } = props;

		return (
			<Link
				className={ textClassNames }
				onClick={ onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined }
				{ ...linkProps }>
				{ children }
			</Link>
		);
	}

	const text =
		<Text
			className={ textClassNames }
			ellipsis={ Boolean(ellipsis) }
			ref={ spanRef }
			{ ...props }>
			{ children }
		</Text>;

	if (!ellipsis) {
		return text;
	}

	const ellipsisTooltip = (isBoolean(ellipsis) ? {} : ellipsis as EllipsisConfig ?? {}).tooltip ?? {};

	const tooltipChildren = React.isValidElement(ellipsisTooltip) ?
		ellipsisTooltip :
		(ellipsisTooltip as TooltipProps).children || children;

	const tooltipProps = (React.isValidElement(ellipsisTooltip) ? {} : ellipsisTooltip) as TooltipProps;

	tooltipProps.children = undefined;

	return (
		<ResizeObserver onResize={ handleResize }>
			<Tooltip
				{ ...tooltipProps }
				overlay={ tooltipChildren }
				placement='bottom'
				showArrow
				trigger={ hasOverflow ? [ 'hover' ] : [] }>
				{ text }
			</Tooltip>
		</ResizeObserver>
	);
};
